import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { renderResponse, type AdaptiveItem } from "./responseRenderers";
import type { AnyValue } from "@/lib/rowTypes";

const ROLE_LABEL: Record<string, string> = {
  CHECK_FOR_UNDERSTANDING: "Check your understanding",
  DIRECT_ASSESSMENT: "Show what you know",
  INDEPENDENT_REASSESSMENT: "On your own this time",
  RETENTION_CHECK: "A quick look back",
  TRANSFER_APPLICATION: "Try this in a new situation",
};

interface Props {
  lessonId: string;
  hintUsed?: boolean;
  onComplete: (sufficient: boolean) => void;
  onExit?: () => void;
}

export default function AdaptiveAssessmentStage({ lessonId, hintUsed = false, onComplete, onExit }: Props) {
  const [cycleId, setCycleId] = useState<string | null>(null);
  const [item, setItem] = useState<AdaptiveItem | null>(null);
  const [feedback, setFeedback] = useState<{ correct: boolean | null; explanation: string | null; uncertain: boolean } | null>(null);
  const [progress, setProgress] = useState({ collected: 0, needed: 0 });
  const [phase, setPhase] = useState<"loading" | "preparing" | "item" | "feedback" | "done" | "gap">("loading");
  const [submitting, setSubmitting] = useState(false);
  const [sufficient, setSufficient] = useState(false);
  const generationPollAttempts = useRef(0);
  const generationPollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const MAX_GENERATION_POLLS = 30;

  const loadNext = useCallback(async (cid: string) => {
    const { data, error } = await supabase.rpc("engine_next_assessment_item", { p_cycle_id: cid });
    if (error) {
      toast.error("We couldn't load the next question. Your progress is saved.");
      setPhase("gap");
      return;
    }
    const res = data as AnyValue;
    if (res?.status === "ITEM") {
      generationPollAttempts.current = 0;
      setItem(res as AdaptiveItem);
      setProgress({ collected: res.progress?.collected ?? 0, needed: res.progress?.needed ?? 0 });
      setFeedback(null);
      setPhase("item");
      return;
    }
    if (res?.status === "CYCLE_COMPLETE" || res?.status === "CYCLE_CLOSED") {
      generationPollAttempts.current = 0;
      setSufficient(Boolean(res?.gate?.sufficient));
      setPhase("done");
      return;
    }
    if (res?.status === "GENERATION_PENDING") {
      setItem(null);
      setFeedback(null);
      setProgress(prev => ({
        collected: res?.progress?.collected ?? prev.collected,
        needed: res?.progress?.needed ?? prev.needed,
      }));
      setPhase("preparing");
      return;
    }
    generationPollAttempts.current = 0;
    setPhase("gap");
  }, []);

  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      const { data, error } = await supabase.rpc("engine_begin_assessment_cycle", { p_lesson_id: lessonId });
      if (cancelled) return;
      const res = data as AnyValue;
      if (error || !res?.cycle_id) {
        setPhase("gap");
        return;
      }
      setCycleId(res.cycle_id as string);
      void loadNext(res.cycle_id as string);
    };
    void start();
    return () => {
      cancelled = true;
      if (generationPollTimer.current) {
        clearTimeout(generationPollTimer.current);
        generationPollTimer.current = null;
      }
    };
  }, [lessonId, loadNext]);

  useEffect(() => {
    if (phase !== "preparing" || !cycleId) return;

    if (generationPollAttempts.current >= MAX_GENERATION_POLLS) {
      setPhase("gap");
      return;
    }

    const delayMs = Math.min(5000, 2500 + generationPollAttempts.current * 100);
    generationPollTimer.current = setTimeout(() => {
      generationPollAttempts.current += 1;
      void loadNext(cycleId);
    }, delayMs);

    return () => {
      if (generationPollTimer.current) {
        clearTimeout(generationPollTimer.current);
        generationPollTimer.current = null;
      }
    };
  }, [phase, cycleId, loadNext]);

  const submit = async (payload: Record<string, unknown>) => {
    if (!item || submitting) return;
    setSubmitting(true);
    const { data, error } = await supabase.rpc("engine_submit_assessment_response", {
      p_exposure_id: item.exposure_id,
      p_response: payload as AnyValue,
      p_hint_used: hintUsed,
      p_idempotency_key: `exposure:${item.exposure_id}`,
    });
    setSubmitting(false);
    if (error) {
      toast.error("We couldn't record that answer. Please try again.");
      return;
    }
    const res = data as AnyValue;
    const uncertain = res?.scoring_status === "EVIDENCE_UNCERTAIN";
    setFeedback({
      correct: typeof res?.is_correct === "boolean" ? res.is_correct : null,
      explanation: res?.explanation ?? null,
      uncertain,
    });
    setProgress({ collected: res?.progress?.collected ?? 0, needed: res?.progress?.needed ?? 0 });
    if (res?.cycle_status === "COMPLETE") setSufficient(Boolean(res?.gate?.sufficient));
    setPhase("feedback");
  };

  if (phase === "loading") {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (phase === "preparing") {
    return (
      <div className="mx-auto flex min-h-[40vh] max-w-md flex-col items-center justify-center gap-4 p-6 text-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <div className="space-y-1">
          <h2 className="font-heading text-lg font-bold text-foreground">Preparing the right check…</h2>
          <p className="font-body text-sm text-muted-foreground">
            We’re matching this to what you just worked on. Nothing is being counted against you while it gets ready.
          </p>
        </div>
      </div>
    );
  }

  if (phase === "gap") {
    return (
      <div className="mx-auto max-w-md space-y-4 p-6 text-center">
        <h2 className="font-heading text-lg font-bold text-foreground">Nothing to check right now</h2>
        <p className="font-body text-sm text-muted-foreground">
          We don't have the right questions ready for this part yet, so nothing counts against you.
        </p>
        <Button className="w-full" onClick={() => (onExit ? onExit() : onComplete(false))}>Go back</Button>
      </div>
    );
  }

  if (phase === "done") {
    return (
      <div className="mx-auto max-w-md space-y-4 p-6 text-center">
        <h2 className="font-heading text-xl font-bold text-foreground">
          {sufficient ? "You've shown this clearly" : "Let's keep building this one"}
        </h2>
        <p className="font-body text-sm text-muted-foreground">
          {sufficient
            ? "Your answers gave us enough to move you forward."
            : "We need a bit more before moving on — you'll get another run at it."}
        </p>
        <Button className="w-full" onClick={() => onComplete(sufficient)}>Continue</Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5 p-4">
      <div className="flex items-center justify-between">
        <p className="font-heading text-xs uppercase tracking-wider text-muted-foreground">
          {ROLE_LABEL[item?.assessment_role ?? ""] ?? "Check your understanding"}
        </p>
        {progress.needed > 0 && (
          <p className="font-heading text-xs text-muted-foreground">
            {Math.min(progress.collected, progress.needed)}/{progress.needed}
          </p>
        )}
      </div>

      <h2 className="font-heading text-base font-bold leading-relaxed text-foreground">{item?.prompt}</h2>

      {phase === "item" && item && renderResponse({ item, disabled: submitting, onSubmit: submit })}

      {phase === "feedback" && (
        <div className="space-y-4">
          <div
            className={cn(
              "rounded-xl border p-4",
              feedback?.uncertain
                ? "border-amber-500/20 bg-amber-500/5"
                : feedback?.correct
                  ? "border-emerald-500/20 bg-emerald-500/5"
                  : "border-amber-500/20 bg-amber-500/5"
            )}
          >
            <p className="mb-1 font-heading text-xs uppercase tracking-wider text-muted-foreground">
              {feedback?.uncertain ? "We need one more look at this" : feedback?.correct ? "Correct" : "Not quite"}
            </p>
            {feedback?.explanation && (
              <p className="font-body text-sm leading-relaxed text-foreground/80">{feedback.explanation}</p>
            )}
          </div>
          <Button className="w-full" onClick={() => (cycleId ? loadNext(cycleId) : onComplete(false))}>
            Continue
          </Button>
        </div>
      )}
    </div>
  );
}
